import { currentUser as queryCurrentUser, userPermissions } from '@/services/system/api';
import { getSessionCookie, removeSessionCookie } from '@/utils/cookie';
import { type Settings as LayoutSettings } from '@ant-design/pro-components';
import '@ant-design/v5-patch-for-react-19';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, useLocation } from '@umijs/max';
import { Avatar } from 'antd';
import { useEffect, useState } from 'react';
import defaultSettings from '../config/defaultSettings';
import { getCurrentPortal, PORTALS } from '../config/portalConfig';
import routes from '../config/routes';
import { AvatarDropdown, ConfigProvider, Header } from './components';
import FirebaseMessaging from './components/FirebaseMessaging';
import { Notification, SystemPortal } from './components/RightContent';
import { findModuleByPath } from './helpers';
import { useImageUrl } from './hooks/useImageUrl';
import ForbiddenPage from './pages/403';
import { errorConfig } from './requestErrorConfig';
const loginPath = '/user/login';
const authPath = '/user/';
const MOBILE_BREAKPOINT = 768;

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.UserData;
  permissions?: API.UserPermissionData[];
  loading?: boolean;
  isMobile?: boolean;
  fetchUserInfo?: () => Promise<
    { currentUser: API.UserData; permissions: API.UserPermissionData[] } | undefined
  >;
}> {
  const fetchUserInfo = async () => {
    try {
      const [currentUser, permissions] = await Promise.all([
        queryCurrentUser({
          skipErrorHandler: true,
        }),
        userPermissions({
          skipErrorHandler: true,
        }),
      ]);
      return {
        currentUser: currentUser.data,
        permissions: permissions.data,
      };
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };

  const { pathname } = history.location;
  // const isLoginPage = pathname === loginPath;
  const isAuthPage = pathname.startsWith(authPath);
  const sessionId = getSessionCookie();

  if (isAuthPage) {
    if (sessionId) {
      try {
        const res = await fetchUserInfo();
        if (res?.currentUser && res?.permissions) {
          history.push('/');
          return {
            fetchUserInfo,
            currentUser: res.currentUser,
            permissions: res.permissions,
            settings: defaultSettings as Partial<LayoutSettings>,
            isMobile: typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT,
          };
        }
      } catch (error) {
        removeSessionCookie();
      }
    }
    return {
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
      isMobile: typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT,
    };
  }

  const res = await fetchUserInfo();
  return {
    fetchUserInfo,
    currentUser: res?.currentUser,
    permissions: res?.permissions,
    settings: defaultSettings as Partial<LayoutSettings>,
    isMobile: typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT,
  };
}

export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const currentModule = findModuleByPath(location.pathname, routes);
  const currentPortal = getCurrentPortal(location.pathname);
  const { imgUrl } = useImageUrl({
    //@ts-ignore
    filePath: initialState?.currentUser?.userProfile?.avatar || null,
  });

  // Listen resize và update initialState
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      if (initialState?.isMobile !== mobile) {
        setInitialState((prev: any) => ({
          ...prev,
          isMobile: mobile,
        }));
      }
    };

    checkMobile(); // Check lần đầu
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [initialState?.isMobile, setInitialState]);

  const isMobileValue = initialState?.isMobile ?? false;
  const mobileActions = isMobileValue ? [<SystemPortal />, <Notification />] : [];
  const mobileAvatar = isMobileValue ? (
    <AvatarDropdown>
      <Avatar src={imgUrl} size={35} style={{ backgroundColor: '#E5E7EB' }} />
    </AvatarDropdown>
  ) : undefined;

  return {
    unAccessible: (
      <ConfigProvider>
        <Header setCollapsed={setCollapsed} collapsed={collapsed} currentModule={currentModule} />
        <div style={{ marginTop: '62px' }}>
          <ForbiddenPage />
        </div>
      </ConfigProvider>
    ),
    formatMessage: undefined,
    collapsed,
    onCollapse: (collapsed: boolean) => {
      setCollapsed(collapsed);
    },
    actionsRender: () => mobileActions,
    avatarProps: {
      render: () => mobileAvatar,
    },
    waterMarkProps: undefined,
    onPageChange: () => {
      if (!initialState?.currentUser && !location.pathname.startsWith(authPath)) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [], //ảnh background
    links: [
      // <Link to='/'>
      //   <SettingOutlined />
      //   {!collapsed && <span>Cài đặt hệ thống</span>}
      // </Link>,
      // <Link to='/'>
      //   <QuestionCircleOutlined />
      //   {!collapsed && <span>Trợ giúp</span>}
      // </Link>,
    ],
    menuHeaderRender: (logo, title) => {
      if (isMobileValue) return undefined;
      return (
        <div className='menu-header'>
          <img src='/images/logo.png' alt='logo' />
          {!collapsed && (
            <div className='menu-header-title'>
              <span>An Lâm ERP</span>
              <span>Hệ thống quản trị doanh nghiệp</span>
            </div>
          )}
        </div>
      );
    },
    menuDataRender: menuData => {
      if (currentPortal) {
        // Chỉ hiển thị menu items có path bắt đầu bằng portal path
        return menuData.filter(item => item.path?.startsWith(currentPortal.path));
      }
      // Menu mặc định khi không có portal
      return menuData.filter(item => !PORTALS.some(portal => item.path?.startsWith(portal.path)));
    },
    childrenRender: children => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <ConfigProvider>
          <FirebaseMessaging />
          <Header setCollapsed={setCollapsed} collapsed={collapsed} currentModule={currentModule} />
          <div className='wrapper-root-content'>{children}</div>
        </ConfigProvider>
      );
    },
    ...initialState?.settings,
  };
};

export const request: RequestConfig = {
  ...errorConfig,
};
